/**
 * This component contains code to show software details when clicked on more info.
 */
import {
  DialogContent,
  IconButton,
  Typography,
  Grid,
  Button,
  DialogActions,
  CardHeader,
  Divider,
  TextField
} from "@mui/material";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import { SoftwareInfoDetail } from "./SoftwareInfoModal.types";
import Settings from "@mui/icons-material/Settings";
import Close from "@mui/icons-material/Close";
import DashedDivider from "common/ui/DashedDivider";
import { defaultTo } from "lodash";
import * as constants from "helpers/constants";
import { useTranslation } from "react-i18next";
import { decode, toDate } from "utils/common";
import { selectSoftwareInfo } from "context/modal.selector";
import { setSoftwareInfo } from "context/modal.context";
import { useAppDispatch, useAppSelector } from "context/hooks";

function SoftwareInfoDetails(props: SoftwareInfoDetail) {
  return (
    <Grid container>
      <Grid container item xs={5} justifyContent="space-between" pr={2}>
        <Typography variant="subtitle1" color="gray.main" fontWeight="medium">
          {props.label}
        </Typography>
        <Divider
          orientation="vertical"
          variant="middle"
          flexItem
          sx={{ borderWidth: 1, borderColor: "gray.dark" }}
        />
      </Grid>
      <Grid item xs={7} paddingLeft={1}>
        <Typography
          variant="subtitle1"
          alignSelf="center"
          color="gray.main"
          title={props.value as string}
          noWrap={props.noWrap}
        >
          {defaultTo(props.value, "-")}
        </Typography>
      </Grid>
    </Grid>
  );
}

function SoftwareInfoModal() {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const props = useAppSelector(selectSoftwareInfo);

  function onModalDismiss() {
    dismiss();
  }

  function dismiss() {
    dispatch(
      setSoftwareInfo({
        open: false
      })
    );
  }

  return (
    <Dialog open={props.open} scroll="body" maxWidth="sm" fullWidth>
      <DialogTitle
        component={CardHeader}
        avatar={<Settings />}
        title={t("softwareInfoModal.softwareDetails")}
        titleTypographyProps={{ variant: "h6" }}
        action={
          <IconButton onClick={onModalDismiss}>
            <Close />
          </IconButton>
        }
        sx={{ color: "gray.main" }}
      />
      <DashedDivider style={{ width: "96%", margin: "auto" }} />
      <DialogContent>
        <SoftwareInfoDetails
          label={t("softwareInfoModal.softwareName")}
          value={props.software?.name}
        />
        <SoftwareInfoDetails
          label={t("softwareInfoModal.softwareType")}
          value={props.software?.softwarePackageCategory.name}
        />
        <SoftwareInfoDetails
          label={t("softwareInfoModal.softwareVersion")}
          value={props.software?.softwareVersion}
        />
        <SoftwareInfoDetails
          label={t("softwareInfoModal.softwarePartId")}
          value={props.software?.softwarePartId}
        />
        <SoftwareInfoDetails
          label={t("softwareInfoModal.softwareConfiguration")}
          value={props.software?.softwareConfiguration}
        />
        <SoftwareInfoDetails
          label={t("softwareInfoModal.machineUse")}
          value={
            props.software?.machineUse?.length
              ? props.software?.machineUse?.map(({ name }) => name).join(", ")
              : "-"
          }
        />
        <SoftwareInfoDetails
          label={t("softwareInfoModal.hardwareVersion")}
          value={
            props.software?.hardwareVersions?.length
              ? props.software?.hardwareVersions
                  ?.map(({ name }) => name)
                  .join(", ")
              : "-"
          }
        />
        <SoftwareInfoDetails
          label={t("softwareInfoModal.dateReleased")}
          value={toDate(props.software?.dateAdded, constants.DATE_FORMAT)}
        />
        <Grid container>
          <Grid item xs={6}>
            <Typography
              variant="subtitle1"
              color="gray.main"
              fontWeight="medium"
            >
              {t("softwareInfoModal.description")}
            </Typography>
          </Grid>
          <TextField
            rows={5}
            multiline={true}
            fullWidth
            value={decode(defaultTo(props.software?.description, ""))}
            disabled
          />
        </Grid>
      </DialogContent>
      <DashedDivider style={{ width: "92%" }} />
      <DialogActions sx={{ display: "flex", justifyContent: "center" }}>
        <Button
          onClick={onModalDismiss}
          variant="outlined"
          size="large"
          startIcon={<Close />}
          color="primary"
        >
          {t("common.close")}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default SoftwareInfoModal;
